import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Image as ImageIcon, Mic, Square, Play, Pause,
  Users as UsersIcon, Plus, Search, Circle, ChevronRight, ChevronDown,
  Download, Home, Share2, User as UserIcon, Gem, DoorOpen, Flag,
  Coins, UserPlus, AtSign, Contact, Video, PhoneOff,
} from "lucide-react";
import { AuthContext, API } from "../App";
import usePwaInstall from "../hooks/usePwaInstall";
import IMVideoCallWindow from "../components/IMVideoCallWindow";
import { requestMedia, toastMediaError } from "../utils/media";
import { checkAndPlayMessageSounds } from "../utils/sounds";
import Avatar from "../components/Avatar";

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const MEGA_LOGO_URL = "/messenger-logo.png";

// Yahoo-style presence colors
const PRESENCE_META = {
  available: { color: "#22c55e", label: "Available", dot: "🟢" },
  away:      { color: "#eab308", label: "Away",      dot: "🟡" },
  busy:      { color: "#ef4444", label: "Busy",      dot: "🔴" },
  invisible: { color: "#94a3b8", label: "Invisible", dot: "⚪" },
  offline:   { color: "#94a3b8", label: "Offline",   dot: "⚫" },
};

// ------- Voice recorder (Web MediaRecorder) --------
// Auto-stops at MAX_VOICE_MS to keep messages small.
const MAX_VOICE_MS = 60_000;
const useVoiceRecorder = (onAutoStop) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const autoStopRef = useRef(null);
  const onAutoStopRef = useRef(onAutoStop);
  useEffect(() => { onAutoStopRef.current = onAutoStop; }, [onAutoStop]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.start();
      recorderRef.current = mr;
      startedAtRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 500);
      // Auto-stop after MAX_VOICE_MS
      autoStopRef.current = setTimeout(() => {
        try { onAutoStopRef.current?.(); } catch (_e) { /* noop */ }
      }, MAX_VOICE_MS);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => new Promise(resolve => {
    const mr = recorderRef.current;
    if (!mr) return resolve(null);
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    mr.onstop = async () => {
      clearInterval(tickRef.current);
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const reader = new FileReader();
      reader.onloadend = () => resolve({
        base64: reader.result,
        duration_ms: Date.now() - startedAtRef.current,
      });
      reader.readAsDataURL(blob);
      streamRef.current?.getTracks().forEach(t => t.stop());
      setRecording(false);
      setElapsed(0);
    };
    mr.stop();
  });

  const cancel = () => {
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (recorderRef.current && recording) {
      recorderRef.current.onstop = () => {};
      recorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
    clearInterval(tickRef.current);
    setRecording(false);
    setElapsed(0);
  };

  return { recording, elapsed, start, stop, cancel, maxMs: MAX_VOICE_MS };
};

// ------- Voice message bubble (waveform + play) -------
const VoiceBubble = ({ src, duration_ms, isSelf }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => { setPlaying(false); setProgress(0); };
    const onTime = () => setProgress(a.currentTime / (a.duration || 1));
    a.addEventListener("ended", onEnd);
    a.addEventListener("timeupdate", onTime);
    return () => {
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("timeupdate", onTime);
    };
  }, [src]);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };
  // Fake waveform bars
  const BARS = 22;
  const secs = duration_ms ? Math.round(duration_ms / 1000) : 0;
  return (
    <div className={`flex items-center gap-2 rounded-full px-3 py-2 ${isSelf ? "bg-purple-500/20" : "bg-yellow-100"}`}>
      <button
        onClick={toggle}
        className={`w-9 h-9 rounded-full flex items-center justify-center ${isSelf ? "bg-purple-600 text-white" : "bg-yellow-400 text-purple-900"} shadow`}
        data-testid="voice-play-btn"
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 pl-0.5" />}
      </button>
      <div className="flex items-end gap-[2px] h-6">
        {Array.from({ length: BARS }).map((_, i) => {
          const activeIdx = Math.floor(progress * BARS);
          const h = 8 + Math.abs(Math.sin(i * 1.7)) * 14;
          const isActive = i <= activeIdx;
          return (
            <div key={i}
              className="w-[3px] rounded-full transition-all"
              style={{ height: `${h}px`, background: isActive ? (isSelf ? "#a855f7" : "#7B0099") : "#cbd5e1" }}
            />
          );
        })}
      </div>
      <span className="text-[10px] text-zinc-500 font-mono min-w-[28px] text-right">{secs}s</span>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
};

const bubbleTint = (senderId) => {
  // Deterministic sender-tinted background from id hash
  let h = 0;
  for (let i = 0; i < senderId.length; i++) h = (h * 31 + senderId.charCodeAt(i)) & 0xffff;
  const hue = h % 360;
  return `hsl(${hue}, 65%, 92%)`;
};

// ------- Buddy list --------
const BuddyList = ({ buddies, onSelect, selectedId, myPresence, onChangeStatus, currentUser, onNewGroup, onOpenContacts }) => {
  const [q, setQ] = useState("");
  const filtered = buddies.filter(b => b.username.toLowerCase().includes(q.toLowerCase()));
  const grouped = filtered.reduce((acc, b) => {
    (acc[b.presence] ||= []).push(b);
    return acc;
  }, {});
  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-purple-50 to-yellow-50 border-r-2 border-purple-300">
      {/* My status */}
      <div className="p-3 bg-gradient-to-r from-purple-700 to-purple-900 text-white">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-purple-900 font-extrabold shadow-inner ring-2 ring-yellow-300">
            {currentUser?.username?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{currentUser?.username}</div>
            <select
              className="mt-0.5 text-[11px] px-2 py-0.5 rounded-full bg-white/90 text-purple-900 font-semibold border-none outline-none cursor-pointer"
              value={myPresence}
              onChange={(e) => onChangeStatus(e.target.value)}
              data-testid="messenger-presence-select"
            >
              {Object.keys(PRESENCE_META).filter(p => p !== "offline").map(p => (
                <option key={p} value={p}>{PRESENCE_META[p].dot} {PRESENCE_META[p].label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Search + new group */}
      <div className="p-2 flex gap-1 border-b border-purple-200">
        <div className="flex-1 min-w-0 flex items-center gap-1 bg-white rounded-full px-3 py-1 border border-purple-200">
          <Search className="w-3 h-3 text-purple-500" />
          <input
            className="flex-1 text-xs outline-none bg-transparent"
            placeholder="Search buddies…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="messenger-search-input"
          />
        </div>
        <button
          onClick={onNewGroup}
          className="rounded-full bg-yellow-400 hover:bg-yellow-300 text-purple-900 p-1.5 border border-purple-300"
          title="Create group"
          data-testid="messenger-new-group-btn"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onOpenContacts}
          className="rounded-full bg-emerald-400 hover:bg-emerald-300 text-emerald-950 p-1.5 border border-emerald-500"
          title="Find friends from your phone contacts"
          data-testid="messenger-contacts-btn"
        >
          <Contact className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Buddies */}
      <div className="flex-1 overflow-y-auto">
        {["available", "away", "busy", "offline"].map(sec => (
          (grouped[sec] || []).length > 0 && (
            <div key={sec}>
              <div className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-purple-700 bg-purple-100 border-b border-purple-200">
                {PRESENCE_META[sec].label} ({grouped[sec].length})
              </div>
              {grouped[sec].map(b => (
                <button
                  key={b.user_id}
                  onClick={() => onSelect({ kind: "dm", user_id: b.user_id, username: b.username, avatar_color: b.avatar_color, avatar: b.avatar })}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-purple-100 transition-colors ${selectedId === `dm:${b.user_id}` ? "bg-yellow-100" : ""}`}
                  data-testid={`buddy-${b.username}`}
                >
                  <div className="relative">
                    <Avatar avatar={b.avatar} username={b.username} color={b.avatar_color} size={32} className="shadow" />
                    <div
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white"
                      style={{ background: PRESENCE_META[b.presence]?.color }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold truncate ${b.presence === "offline" ? "text-zinc-400" : "text-purple-900"}`}>
                      {b.username}
                    </div>
                    {b.status_message && (
                      <div className="text-[10px] text-zinc-500 italic truncate">{b.status_message}</div>
                    )}
                  </div>
                  <ChevronRight className="w-3 h-3 text-purple-300" />
                </button>
              ))}
            </div>
          )
        ))}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-zinc-500">
            No buddies yet. Add friends from anyone&apos;s profile.
          </div>
        )}
      </div>
    </div>
  );
};

// ------- Conversation view -------
// ------- 1:1 WebRTC video call (signaling relayed over the messenger WS) -------
const MessengerVideoCall = ({ targetUserId, targetUsername, wsRef, mode, initialOffer, onClose }) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connected, setConnected] = useState(false);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const endedRef = useRef(false);
  const pendingIce = useRef([]);

  const send = (type, payload = {}) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type, target_user_id: targetUserId, ...payload }));
    }
  };

  const endCall = (remote = false) => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (!remote) send("webrtc_end");
    try { pcRef.current?.close(); } catch (_) { /* noop */ }
    pcRef.current = null;
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    setRemoteStream(null);
    onClose();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await requestMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        localStreamRef.current = stream;
        setLocalStream(stream);

        const pc = new RTCPeerConnection(RTC_CONFIG);
        pcRef.current = pc;
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        pc.ontrack = (e) => { setRemoteStream(e.streams[0]); setConnected(true); };
        pc.onicecandidate = (e) => { if (e.candidate) send("webrtc_ice", { candidate: e.candidate }); };
        pc.oniceconnectionstatechange = () => {
          const st = pc.iceConnectionState;
          if (st === "connected" || st === "completed") setConnected(true);
          if (st === "failed" || st === "closed") setConnected(false);
        };

        if (mode === "caller") {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          send("webrtc_offer", { offer });
        } else if (mode === "callee" && initialOffer) {
          await pc.setRemoteDescription(new RTCSessionDescription(initialOffer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send("webrtc_answer", { answer });
          // flush any queued ICE
          for (const c of pendingIce.current) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (_) { /* noop */ }
          }
          pendingIce.current = [];
        }
      } catch (e) {
        toastMediaError(e);
        endCall(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signaling from the other peer (relayed via messenger WS -> window event)
  useEffect(() => {
    const onEvt = async (ev) => {
      const d = ev.detail;
      if (!d || d.from_user_id !== targetUserId) return;
      const pc = pcRef.current;
      if (d.type === "webrtc_answer" && pc && d.answer) {
        try { await pc.setRemoteDescription(new RTCSessionDescription(d.answer)); } catch (_) { /* noop */ }
      } else if (d.type === "webrtc_ice" && d.candidate) {
        if (pc && pc.remoteDescription) {
          try { await pc.addIceCandidate(new RTCIceCandidate(d.candidate)); } catch (_) { /* noop */ }
        } else {
          pendingIce.current.push(d.candidate);
        }
      } else if (d.type === "webrtc_end") {
        endCall(true);
      }
    };
    window.addEventListener("messenger:event", onEvt);
    return () => window.removeEventListener("messenger:event", onEvt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  useEffect(() => () => { endCall(false); }, []); // cleanup on unmount

  return (
    <IMVideoCallWindow
      targetUserId={targetUserId}
      targetUsername={targetUsername}
      localStream={localStream}
      remoteStream={remoteStream}
      isTestMode={false}
      webcamConnected={connected}
      onStartCall={() => {}}
      onEndCall={() => endCall(false)}
      onClose={() => endCall(false)}
    />
  );
};


const ConversationView = ({ conversation, currentUser, token, wsRef, onMessageSent }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [typingFrom, setTypingFrom] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'tip' | 'room' | 'report'
  const [callMode, setCallMode] = useState(null); // null | 'caller' | 'callee'
  const [incomingOffer, setIncomingOffer] = useState(null); // {offer, from_username}
  const headerRef = useRef(null);
  const typingTimeout = useRef(null);
  const scrollRef = useRef(null);
  const recorder = useVoiceRecorder(async () => {
    // Auto-stop at 60s → auto-send
    const rec = await recorder.stop();
    if (rec?.base64) {
      await sendPayload({ message_type: "voice", content: rec.base64, duration_ms: rec.duration_ms });
      toast.info("Voice note stopped at 60s (max length) and sent.");
    }
  });
  const fileInputRef = useRef(null);

  const conversationId = conversation?.kind === "dm"
    ? `dm:${[currentUser.user_id, conversation.user_id].sort().join(":")}`
    : conversation?.conversation_id;

  const load = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messenger/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error("Couldn't load conversation");
    } finally {
      setLoading(false);
    }
  }, [conversationId, token]);

  useEffect(() => { load(); }, [load]);

  // Live incoming messages via parent WS ref
  useEffect(() => {
    const onWSMessage = (ev) => {
      const data = ev.detail;
      if (!data) return;
      if (data.type === "message") {
        if (data.conversation_id === conversationId) {
          setMessages(prev => prev.some(x => x.message_id === data.message_id) ? prev : [...prev, data]);
          scrollBottom();
          // Play keyword sound clips on incoming messages — same as chat rooms
          if (data.message_type === "text" && data.content) {
            checkAndPlayMessageSounds(data.content);
          }
        }
      } else if (data.type === "typing" && data.conversation_id === conversationId) {
        setTypingFrom(data.user_id);
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setTypingFrom(null), 2500);
      } else if (data.type === "webrtc_offer" && data.from_user_id === conversation?.user_id) {
        // Incoming video call from the person we're chatting with
        setCallMode(prev => prev ? prev : null);
        setIncomingOffer({ offer: data.offer, from_username: data.from_username });
      } else if (data.type === "webrtc_end" && data.from_user_id === conversation?.user_id) {
        setIncomingOffer(null);
      }
    };
    window.addEventListener("messenger:event", onWSMessage);
    return () => window.removeEventListener("messenger:event", onWSMessage);
  }, [conversationId, conversation]);

  const scrollBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, 50);
  };
  useEffect(scrollBottom, [messages.length]);

  const sendText = async () => {
    const content = input.trim();
    if (!content) return;
    setInput("");
    // Play keyword sound clips (wtf, xoxo, middle-finger) — same as chat rooms
    checkAndPlayMessageSounds(content);
    await sendPayload({ message_type: "text", content });
  };

  const sendPayload = async (payload) => {
    try {
      const body = {
        ...payload,
        ...(conversation.kind === "dm"
          ? { recipient_id: conversation.user_id }
          : { conversation_id: conversation.conversation_id }),
      };
      const res = await axios.post(`${API}/messenger/messages`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Optimistic append if not echoed by WS
      const m = res.data.message;
      setMessages(prev => prev.some(x => x.message_id === m.message_id) ? prev : [...prev, m]);
      onMessageSent?.(m);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_400_000) {
      toast.error("Image too large (max ~1.4MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      await sendPayload({ message_type: "image", content: reader.result });
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.readAsDataURL(f);
  };

  const toggleVoice = async () => {
    if (!recorder.recording) {
      await recorder.start();
    } else {
      const rec = await recorder.stop();
      if (rec?.base64) {
        await sendPayload({ message_type: "voice", content: rec.base64, duration_ms: rec.duration_ms });
      }
    }
  };

  const emitTyping = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify({ type: "typing", conversation_id: conversationId })); } catch (_) { /* noop */ }
  };

  const isDm = conversation?.kind === "dm";
  const targetUserId = conversation?.user_id;
  const targetUsername = conversation?.username;

  const goToProfile = () => {
    setMenuOpen(false);
    if (targetUserId) navigate(`/profile/${targetUserId}`);
  };

  const handleAddFriend = async () => {
    setMenuOpen(false);
    if (!targetUserId) return;
    try {
      await axios.post(`${API}/friends/request`,
        { friend_user_id: targetUserId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Friend request sent to ${targetUsername}!`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send friend request");
    }
  };

  // Close header menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 via-yellow-50 to-purple-100">
        <img src={MEGA_LOGO_URL} alt="Mega Chat Messenger" className="h-28 w-28 object-contain drop-shadow-xl" data-testid="messenger-entry-logo" />
        <p className="text-sm text-purple-600 mt-4">← Pick a buddy or start a new group to chat</p>
      </div>
    );
  }

  const title = conversation.kind === "dm" ? conversation.username : conversation.name;

  return (
    <div className="relative flex-1 flex flex-col bg-white min-h-0">
      {/* Header */}
      <div ref={headerRef} className="relative bg-gradient-to-r from-purple-800 to-purple-900 text-white px-2 py-1.5 flex items-center gap-2 shadow-md flex-shrink-0">
        <button
          type="button"
          onClick={() => isDm && setMenuOpen(o => !o)}
          className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center text-purple-900 font-extrabold text-xs flex-shrink-0 overflow-hidden"
          title={isDm ? "Options" : ""}
          data-testid="messenger-header-avatar-btn"
        >
          {conversation.kind === "dm"
            ? <Avatar avatar={conversation.avatar} username={conversation.username} color={conversation.avatar_color || "#7B0099"} size={28} />
            : <UsersIcon className="w-3 h-3" />}
        </button>
        {isDm ? (
          <button
            className="flex-1 min-w-0 text-left group"
            onClick={() => setMenuOpen(o => !o)}
            onDoubleClick={goToProfile}
            title="Click for options · double-click to view profile"
            data-testid="messenger-header-name-btn"
          >
            <div className="font-bold text-xs flex items-center gap-1">
              <span className="truncate group-hover:underline">{title}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </div>
            <div className="text-[9px] text-purple-200">1:1 chat · tap name for options</div>
          </button>
        ) : (
          <div className="flex-1 min-w-0">
            <div className="font-bold text-xs">{title}</div>
            <div className="text-[9px] text-purple-200">
              {`${(conversation.member_ids || []).length} members`}
            </div>
          </div>
        )}
        {isDm && (
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setIncomingOffer(null); setCallMode("caller"); }}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center text-white shadow"
            title={`Start a video call with ${targetUsername}`}
            data-testid="messenger-webcam-btn"
          >
            <Video className="w-4 h-4" />
          </button>
        )}
        {isDm && menuOpen && (
          <div
            className="absolute left-9 top-11 z-50 w-60 bg-white rounded-xl shadow-2xl border-2 border-purple-300 overflow-hidden text-purple-900 max-h-[70vh] overflow-y-auto"
            data-testid="messenger-header-menu"
          >
            <div className="px-3 py-2 bg-gradient-to-r from-purple-700 to-purple-900 text-white text-xs font-bold truncate sticky top-0">
              {targetUsername}
            </div>
            <button onClick={goToProfile} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-view-profile">
              <UserIcon className="w-4 h-4 text-purple-600" /> View Profile
            </button>
            <button onClick={() => { setMenuOpen(false); setActiveModal("tip"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-send-diamonds">
              <Gem className="w-4 h-4 text-cyan-500" /> Send Diamonds
            </button>
            <button onClick={() => { setMenuOpen(false); setActiveModal("fchips"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-send-fchips">
              <Coins className="w-4 h-4 text-amber-500" /> Send F-Chips
            </button>
            <button onClick={handleAddFriend} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-add-friend">
              <UserPlus className="w-4 h-4 text-emerald-600" /> Add Friend
            </button>
            <button onClick={() => { setMenuOpen(false); setActiveModal("room"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-invite-room">
              <DoorOpen className="w-4 h-4 text-purple-600" /> Invite to a Chat Room
            </button>
            <button onClick={() => { setMenuOpen(false); setActiveModal("reqinfo"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-purple-50 text-left" data-testid="hdr-request-info">
              <AtSign className="w-4 h-4 text-indigo-600" /> Request Contact Info
            </button>
            <div className="border-t border-purple-100" />
            <button onClick={() => { setMenuOpen(false); setActiveModal("report"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-red-50 text-red-600 text-left" data-testid="hdr-report-user">
              <Flag className="w-4 h-4" /> Report User
            </button>
          </div>
        )}
      </div>
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-gradient-to-b from-yellow-50/60 to-purple-50/60 min-h-0" data-testid="messenger-messages-scroll">
        {loading && <p className="text-center text-xs text-zinc-500">Loading…</p>}
        {!loading && messages.length === 0 && (
          <p className="text-center text-xs text-zinc-400 py-12">Say hi 👋</p>
        )}
        {messages.map(m => {
          const isSelf = m.sender_id === currentUser.user_id;
          const tint = isSelf ? "#7B0099" : bubbleTint(m.sender_id);
          return (
            <div key={m.message_id} className={`flex ${isSelf ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[70%] rounded-2xl px-3 py-2 shadow-sm ${isSelf ? "text-white rounded-br-md" : "text-zinc-800 rounded-bl-md"}`}
                style={{ background: tint }}
                data-testid={`msg-${m.message_id}`}
              >
                {conversation.kind === "group" && !isSelf && (
                  <div className="text-[10px] font-bold text-purple-900 mb-0.5">{m.sender_username}</div>
                )}
                {m.message_type === "text" && <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>}
                {m.message_type === "image" && (
                  <img src={m.content} alt="attachment" className="max-w-full max-h-64 rounded-lg" />
                )}
                {m.message_type === "voice" && (
                  <VoiceBubble src={m.content} duration_ms={m.duration_ms} isSelf={isSelf} />
                )}
                <div className={`text-[9px] mt-1 ${isSelf ? "text-purple-100" : "text-zinc-500"}`}>
                  {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        {typingFrom && (
          <div className="text-[10px] text-purple-600 italic">Someone is typing…</div>
        )}
      </div>
      {/* Composer — compact pill */}
      <div className="px-2 py-1.5 border-t-2 border-purple-200 bg-purple-50 flex-shrink-0">
        {recorder.recording ? (
          <div className="flex items-center gap-2 bg-red-100 rounded-full px-3 py-2 border-2 border-red-300 animate-pulse">
            <Mic className="w-4 h-4 text-red-600" />
            <span className="text-xs font-bold text-red-700 flex-1">
              Recording… {recorder.elapsed}s <span className="opacity-70">/ 60s</span>
            </span>
            <button onClick={recorder.cancel} className="text-xs text-red-600 font-bold">Cancel</button>
            <button
              onClick={toggleVoice}
              className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-full"
              data-testid="voice-stop-btn"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-white rounded-full px-3 py-2 border-2 border-purple-300 shadow-inner">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-purple-500 hover:text-purple-700 p-1"
              title="Send image"
              data-testid="attach-image-btn"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFile} className="hidden" />
            <input
              className="flex-1 text-sm outline-none bg-transparent"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => { setInput(e.target.value); emitTyping(); }}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendText())}
              data-testid="messenger-input"
            />
            <button
              onClick={toggleVoice}
              className="text-purple-500 hover:text-purple-700 p-1"
              title="Voice message"
              data-testid="voice-record-btn"
            >
              <Mic className="w-4 h-4" />
            </button>
            <button
              onClick={sendText}
              disabled={!input.trim()}
              className="bg-gradient-to-br from-purple-600 to-purple-800 text-white p-2 rounded-full disabled:opacity-40 shadow"
              data-testid="messenger-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {activeModal && isDm && (
        <ConvActionModal
          type={activeModal}
          targetUserId={targetUserId}
          targetUsername={targetUsername}
          token={token}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Incoming video call banner */}
      {isDm && incomingOffer && !callMode && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-[70] w-[92%] max-w-sm bg-white rounded-2xl shadow-2xl border-2 border-green-400 p-3 flex items-center gap-3" data-testid="messenger-incoming-call">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white animate-pulse">
            <Video className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-purple-900 truncate">{incomingOffer.from_username || targetUsername}</div>
            <div className="text-[11px] text-green-700">Incoming video call…</div>
          </div>
          <button
            onClick={() => setCallMode("callee")}
            className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-bold"
            data-testid="messenger-accept-call-btn"
          >
            Accept
          </button>
          <button
            onClick={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "webrtc_end", target_user_id: targetUserId }));
              setIncomingOffer(null);
            }}
            className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center"
            data-testid="messenger-decline-call-btn"
          >
            <PhoneOff className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Active video call */}
      {isDm && callMode && (
        <MessengerVideoCall
          targetUserId={targetUserId}
          targetUsername={targetUsername}
          wsRef={wsRef}
          mode={callMode}
          initialOffer={callMode === "callee" ? incomingOffer?.offer : null}
          onClose={() => { setCallMode(null); setIncomingOffer(null); }}
        />
      )}
    </div>
  );
};

// ------- Conversation header action modals (tip / room invite / report) -------
const ConvActionModal = ({ type, targetUserId, targetUsername, token, onClose }) => {
  const [amount, setAmount] = useState(10);
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (type !== "room") return;
    axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const cats = res.data.categories || {};
        const list = [];
        const seen = new Set();
        const addRoom = (r) => {
          if (r.room_id && r.name && r.can_access !== false && !seen.has(r.room_id)) {
            seen.add(r.room_id);
            list.push(r);
          }
        };
        Object.values(cats).forEach(cat => {
          (cat.rooms || []).forEach(addRoom);
          Object.values(cat.subcategories || {}).forEach(sub => (sub.rooms || []).forEach(addRoom));
        });
        setRooms(list);
        if (list[0]) setRoomId(list[0].room_id);
      })
      .catch(() => setRooms([]));
  }, [type, token]);

  const sendTip = async () => {
    if (amount <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/diamonds/tip`,
        { to_username: targetUsername, amount: Number(amount), message },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent ${amount} 💎 to ${targetUsername}!`);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send diamonds");
    } finally { setBusy(false); }
  };

  const sendFchips = async () => {
    if (Number(amount) < 10) { toast.error("Minimum is 10 F-Chips"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/fchips/tip`,
        { to_username: targetUsername, amount: Number(amount), message },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent ${amount} 🎰 F-Chips to ${targetUsername}!`);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send F-Chips");
    } finally { setBusy(false); }
  };

  const requestInfo = async (field) => {
    setBusy(true);
    try {
      await axios.post(`${API}/messenger/contacts/request-info`,
        { target_user_id: targetUserId, field },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Asked ${targetUsername} to share their ${field}.`);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send request");
    } finally { setBusy(false); }
  };

  const sendRoomInvite = async () => {
    if (!roomId) { toast.error("Pick a room"); return; }
    const room = rooms.find(r => r.room_id === roomId);
    setBusy(true);
    try {
      await axios.post(`${API}/room-invite`,
        { target_user_id: targetUserId, room_id: roomId, room_name: room?.name || "a chat room" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invited ${targetUsername} to ${room?.name || "the room"}!`);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send invite");
    } finally { setBusy(false); }
  };

  const sendReport = async () => {
    if (!reason.trim()) { toast.error("Please describe the issue"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/users/report`,
        { reported_user_id: targetUserId, reason, source: "messenger" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Report submitted. Staff will review it.");
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit report");
    } finally { setBusy(false); }
  };

  const titles = { tip: "Send Diamonds", fchips: "Send F-Chips", room: "Invite to a Chat Room", report: "Report User", reqinfo: "Request Contact Info" };

  return (
    <div className="absolute inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="conv-action-modal">
      <div className="w-full max-w-xs bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-2.5 bg-gradient-to-r from-purple-800 to-purple-900 text-white font-bold text-sm">
          {titles[type]} <span className="text-purple-200 font-normal">· {targetUsername}</span>
        </div>
        <div className="p-4 space-y-3">
          {type === "tip" && (
            <>
              <label className="block text-xs font-bold text-purple-800">Amount (💎)</label>
              <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full border-2 border-purple-200 rounded-lg px-3 py-2 text-sm" data-testid="tip-amount-input" />
              <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Optional note…"
                className="w-full border-2 border-purple-200 rounded-lg px-3 py-2 text-sm" data-testid="tip-message-input" />
              <button onClick={sendTip} disabled={busy}
                className="w-full py-2 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-bold text-sm disabled:opacity-40" data-testid="tip-send-btn">
                {busy ? "Sending…" : `Send ${amount} 💎`}
              </button>
            </>
          )}
          {type === "fchips" && (
            <>
              <label className="block text-xs font-bold text-amber-700">Amount (🎰 F-Chips) · min 10</label>
              <input type="number" min="10" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full border-2 border-amber-200 rounded-lg px-3 py-2 text-sm" data-testid="fchips-amount-input" />
              <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Optional note…"
                className="w-full border-2 border-amber-200 rounded-lg px-3 py-2 text-sm" data-testid="fchips-message-input" />
              <button onClick={sendFchips} disabled={busy}
                className="w-full py-2 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white font-bold text-sm disabled:opacity-40" data-testid="fchips-send-btn">
                {busy ? "Sending…" : `Send ${amount} 🎰`}
              </button>
            </>
          )}
          {type === "reqinfo" && (
            <>
              <p className="text-xs text-purple-700">Ask {targetUsername} to share their contact so you can connect off MEGA Chat too.</p>
              <button onClick={() => requestInfo("email")} disabled={busy}
                className="w-full py-2 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-bold text-sm disabled:opacity-40" data-testid="reqinfo-email-btn">
                Request Email
              </button>
              <button onClick={() => requestInfo("phone")} disabled={busy}
                className="w-full py-2 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white font-bold text-sm disabled:opacity-40" data-testid="reqinfo-phone-btn">
                Request Phone Number
              </button>
            </>
          )}
          {type === "room" && (
            <>
              <label className="block text-xs font-bold text-purple-800">Choose a room</label>
              <select value={roomId} onChange={e => setRoomId(e.target.value)}
                className="w-full border-2 border-purple-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="room-select">
                {rooms.length === 0 && <option value="">No rooms available</option>}
                {rooms.map(r => <option key={r.room_id} value={r.room_id}>{r.name}</option>)}
              </select>
              <button onClick={sendRoomInvite} disabled={busy || !roomId}
                className="w-full py-2 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-bold text-sm disabled:opacity-40" data-testid="room-invite-send-btn">
                {busy ? "Sending…" : "Send Invite"}
              </button>
            </>
          )}
          {type === "report" && (
            <>
              <label className="block text-xs font-bold text-red-700">Reason</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4}
                placeholder="Describe what happened…"
                className="w-full border-2 border-red-200 rounded-lg px-3 py-2 text-sm" data-testid="report-reason-input" />
              <button onClick={sendReport} disabled={busy}
                className="w-full py-2 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white font-bold text-sm disabled:opacity-40" data-testid="report-send-btn">
                {busy ? "Submitting…" : "Submit Report"}
              </button>
            </>
          )}
          <button onClick={onClose} className="w-full py-1.5 text-xs text-purple-600 font-bold" data-testid="conv-action-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
};
const NewGroupModal = ({ buddies, onClose, onCreate }) => {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(new Set());
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-4" onClick={(e) => e.stopPropagation()} data-testid="new-group-modal">
        <h3 className="text-lg font-black text-purple-900 mb-2">Create Group Chat</h3>
        <input
          className="w-full rounded-full border-2 border-purple-300 px-3 py-2 text-sm outline-none mb-3"
          placeholder="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="group-name-input"
        />
        <div className="text-xs font-bold text-purple-700 mb-1">Choose buddies:</div>
        <div className="max-h-56 overflow-y-auto space-y-1 mb-3 border border-purple-200 rounded-xl p-2">
          {buddies.map(b => (
            <label key={b.user_id} className="flex items-center gap-2 p-1 rounded hover:bg-purple-50 cursor-pointer">
              <input type="checkbox" checked={selected.has(b.user_id)} onChange={() => toggle(b.user_id)} />
              <span className="text-sm">{b.username}</span>
            </label>
          ))}
          {buddies.length === 0 && <p className="text-xs text-zinc-500 text-center py-2">No buddies to add</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-full border-2 border-purple-300 text-purple-700 font-bold text-sm">Cancel</button>
          <button
            onClick={() => onCreate(name, [...selected])}
            disabled={!name.trim() || selected.size === 0}
            className="flex-1 py-2 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold text-sm disabled:opacity-40"
            data-testid="group-create-submit"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

// ------- iOS install help modal -------
const IosInstallHelp = ({ onClose }) => (
  <div className="fixed inset-0 z-[10000] bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
    <div
      className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md p-5"
      onClick={(e) => e.stopPropagation()}
      data-testid="ios-install-help"
    >
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-5 h-5 text-purple-800" />
        <h3 className="text-lg font-black text-purple-900">Install on iPhone / iPad</h3>
      </div>
      <ol className="space-y-2 text-sm text-zinc-700 list-decimal ml-5">
        <li>Tap the <b>Share</b> button in Safari&apos;s bottom toolbar (the square with an up-arrow).</li>
        <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
        <li>Tap <b>Add</b> in the top-right — Messenger will appear as a real app icon on your home screen.</li>
      </ol>
      <p className="mt-3 text-xs text-zinc-500">
        Note: iOS only supports &quot;Add to Home Screen&quot; from <b>Safari</b>. Chrome/Firefox on iPhone can&apos;t install PWAs.
      </p>
      <button
        onClick={onClose}
        className="mt-4 w-full py-2 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold text-sm"
        data-testid="ios-install-help-close"
      >
        Got it
      </button>
    </div>
  </div>
);

// ------- Contacts panel (phone contact import + matching, Snapchat/WhatsApp style) -------
const ContactsPanel = ({ token, currentUser, onClose, onStartChat, onImported }) => {
  const [phone, setPhone] = useState(currentUser?.phone_number || "");
  const [savingPhone, setSavingPhone] = useState(false);
  const [onMega, setOnMega] = useState([]);
  const [toInvite, setToInvite] = useState([]);
  const [imported, setImported] = useState(false);
  const [importing, setImporting] = useState(false);
  const supported = typeof navigator !== "undefined" && navigator.contacts && typeof navigator.contacts.select === "function";
  const inviteLink = `${window.location.origin}/messenger?app=messenger&ref=${encodeURIComponent(currentUser?.username || "")}`;

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data?.phone_number) setPhone(r.data.phone_number); })
      .catch(() => {});
    axios.get(`${API}/messenger/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setOnMega(r.data.on_megachat || []); setToInvite(r.data.not_on_megachat || []); setImported(!!r.data.imported); })
      .catch(() => {});
  }, [token]);

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await axios.put(`${API}/users/profile`, { phone_number: phone }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Your number was saved — friends can now find you.");
    } catch (e) {
      toast.error("Couldn't save number");
    } finally { setSavingPhone(false); }
  };

  const importContacts = async () => {
    if (!supported) {
      toast.error("Contact import needs Android Chrome. Use the invite link below to invite friends.");
      return;
    }
    setImporting(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });
      if (!picked || picked.length === 0) { setImporting(false); return; }
      const contacts = picked.map(c => ({
        name: (c.name && c.name[0]) || "",
        phones: c.tel || [],
        emails: c.email || [],
      }));
      const res = await axios.post(`${API}/messenger/contacts/import`, { contacts, add_as_friends: true },
        { headers: { Authorization: `Bearer ${token}` } });
      setOnMega(res.data.on_megachat || []);
      setToInvite(res.data.not_on_megachat || []);
      setImported(true);
      const { matched = 0, to_invite = 0 } = res.data.counts || {};
      toast.success(`Found ${matched} on MEGA Chat · ${to_invite} to invite`);
      onImported?.();
    } catch (e) {
      if (e?.name === "SecurityError" || e?.name === "NotAllowedError") {
        toast.error("Contact access was blocked.");
      } else {
        toast.error("Contact import cancelled or unavailable.");
      }
    } finally { setImporting(false); }
  };

  const shareInvite = async (name) => {
    const text = `Hey${name ? " " + name : ""}! I'm on MEGA Chat Messenger — join me: ${inviteLink}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Join me on MEGA Chat", text, url: inviteLink });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Invite link copied!");
      }
    } catch (_) { /* user cancelled share */ }
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="messenger-contacts-panel">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-purple-700 text-white flex items-center gap-2">
          <Contact className="w-5 h-5" />
          <h3 className="font-black text-base flex-1">Find Friends</h3>
          <button onClick={onClose} className="text-white/90 text-lg leading-none" data-testid="contacts-close-btn">×</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          {/* Your number */}
          <div>
            <label className="block text-xs font-bold text-purple-800 mb-1">Your phone number (so friends can find you)</label>
            <div className="flex gap-2">
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 123 4567"
                className="flex-1 border-2 border-purple-200 rounded-lg px-3 py-2 text-sm" data-testid="contacts-phone-input" />
              <button onClick={savePhone} disabled={savingPhone}
                className="px-4 py-2 rounded-lg bg-purple-700 text-white font-bold text-sm disabled:opacity-40" data-testid="contacts-phone-save-btn">
                {savingPhone ? "…" : "Save"}
              </button>
            </div>
          </div>

          {/* Import button */}
          <button onClick={importContacts} disabled={importing}
            className="w-full py-3 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-black text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contacts-import-btn">
            <Contact className="w-4 h-4" /> {importing ? "Importing…" : "Select Contacts to Import"}
          </button>
          {supported && (
            <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="contacts-multiselect-tip">
              💡 <b>Tip:</b> Your phone's contact picker lets you <b>select multiple people</b> — check each name (or tap <b>“Select all”</b> if your phone shows it), then tap <b>Done</b>. Everyone you pick gets imported at once.
            </p>
          )}
          {!supported && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="contacts-unsupported-note">
              Contact import works on <b>Android Chrome</b>. On other devices, share your invite link below to bring friends in.
            </p>
          )}

          {/* On MEGA Chat */}
          {imported && (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 mb-1">On MEGA Chat ({onMega.length})</div>
              {onMega.length === 0 && <p className="text-xs text-zinc-400">None of your contacts are on MEGA Chat yet.</p>}
              <div className="space-y-1.5">
                {onMega.map(m => (
                  <div key={m.user_id} className="flex items-center gap-2 bg-emerald-50 rounded-xl p-2" data-testid={`contact-mega-${m.username}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: m.avatar_color }}>
                      {m.username[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-purple-900 truncate">{m.username}</div>
                      {m.contact_name && <div className="text-[10px] text-zinc-500 truncate">from contacts: {m.contact_name}</div>}
                    </div>
                    <button onClick={() => { onStartChat({ kind: "dm", user_id: m.user_id, username: m.username, avatar_color: m.avatar_color, avatar: m.avatar }); onClose(); }}
                      className="px-3 py-1.5 rounded-full bg-purple-700 text-white text-xs font-bold" data-testid={`contact-message-${m.username}`}>
                      Message
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* To invite */}
          {imported && (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-wider text-purple-700 mb-1">Invite to MEGA Chat ({toInvite.length})</div>
              <div className="space-y-1.5">
                {toInvite.slice(0, 50).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 bg-purple-50 rounded-xl p-2" data-testid={`contact-invite-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-xs">
                      {(c.name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-purple-900 truncate">{c.name || c.phone || c.email}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{c.phone || c.email}</div>
                    </div>
                    <button onClick={() => shareInvite(c.name)}
                      className="px-3 py-1.5 rounded-full bg-yellow-400 text-purple-900 text-xs font-bold flex items-center gap-1" data-testid={`contact-invite-btn-${i}`}>
                      <Share2 className="w-3 h-3" /> Invite
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Universal share link */}
          <div className="border-t border-purple-100 pt-3">
            <div className="text-xs font-bold text-purple-800 mb-1">Your invite link</div>
            <div className="flex gap-2">
              <input readOnly value={inviteLink} className="flex-1 border-2 border-purple-200 rounded-lg px-3 py-2 text-xs bg-purple-50" data-testid="contacts-invite-link" />
              <button onClick={() => shareInvite("")} className="px-4 py-2 rounded-lg bg-yellow-400 text-purple-900 font-bold text-sm flex items-center gap-1" data-testid="contacts-share-link-btn">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------- Main Page -------
const MessengerPage = ({ onClose }) => {
  const { user: currentUser, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const isModal = typeof onClose === "function";
  const { installed, canInstall, isIos, promptInstall } = usePwaInstall();
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // { kind, user_id/conversation_id, ... }
  const [myPresence, setMyPresence] = useState(currentUser?.messenger_presence || "available");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const wsRef = useRef(null);

  const handleInstall = async () => {
    if (isIos) { setShowIosHelp(true); return; }
    const res = await promptInstall();
    if (res?.outcome === "accepted") toast.success("Messenger installed!");
    else if (res?.outcome === "unavailable") {
      toast.message("Install prompt not ready", { description: "Open the site in Chrome/Edge/Brave on your phone and try again." });
    }
  };
  const goToRooms = () => {
    if (isModal) { onClose?.(); return; }
    navigate("/");
  };

  // Sync presence with the server on mount so the dropdown shows the persisted value
  // (currentUser from initial /auth/me may be stale after long sessions).
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        const p = res.data?.messenger_presence;
        if (p && ["available", "away", "busy", "invisible", "offline"].includes(p)) {
          setMyPresence(p);
        }
      } catch (_e) { /* noop */ }
    })();
  }, [token]);

  const loadBuddies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messenger/buddies`, { headers: { Authorization: `Bearer ${token}` } });
      setBuddies(res.data.buddies || []);
    } catch (_) { /* noop */ }
  }, [token]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messenger/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data.conversations || []);
    } catch (_) { /* noop */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadBuddies();
    loadConversations();
    const iv = setInterval(loadBuddies, 20_000); // periodic presence refresh
    return () => clearInterval(iv);
  }, [token, loadBuddies, loadConversations]);

  // Messenger WS
  useEffect(() => {
    if (!token) return;
    const wsUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")}/api/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        window.dispatchEvent(new CustomEvent("messenger:event", { detail: data }));
        if (data.type === "message") {
          loadConversations();
        } else if (data.type === "presence_update") {
          setBuddies(prev => prev.map(b => b.user_id === data.user_id
            ? { ...b, presence: data.presence, status_message: data.status_message || b.status_message }
            : b
          ));
        } else if (data.type === "group_created") {
          loadConversations();
        }
      } catch (_) { /* noop */ }
    };
    const pingIv = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
    }, 25_000);
    return () => { clearInterval(pingIv); try { ws.close(); } catch (_e) { /* noop */ } };
  }, [token, loadConversations]);

  const changeStatus = async (presence) => {
    setMyPresence(presence);
    try {
      localStorage.setItem("messenger_presence", presence);
    } catch (_e) { /* noop */ }
    try {
      await axios.put(`${API}/messenger/status`, { presence }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_) {
      toast.error("Couldn't update status");
    }
  };

  const createGroup = async (name, memberIds) => {
    try {
      const res = await axios.post(`${API}/messenger/groups`,
        { name, member_ids: memberIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const g = res.data.group;
      setShowNewGroup(false);
      setSelected({ kind: "group", conversation_id: g.conversation_id, name: g.name, member_ids: g.member_ids });
      loadConversations();
      toast.success(`Group "${g.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group");
    }
  };

  const selectedId = selected
    ? (selected.kind === "dm" ? `dm:${[currentUser.user_id, selected.user_id].sort().join(":")}` : selected.conversation_id)
    : null;

  return (
    <div className={isModal ? "h-full w-full flex flex-col bg-white overflow-hidden" : "fixed inset-x-0 top-0 flex flex-col bg-white overflow-hidden"} style={isModal ? undefined : { height: "100dvh" }}>
      {/* Top bar */}
      <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-yellow-600 text-white px-3 py-1.5 flex items-center gap-2 shadow-md flex-shrink-0">
        <button
          onClick={() => isModal ? onClose() : navigate("/")}
          className="p-1 rounded-full bg-white/20 hover:bg-white/30"
          title={isModal ? "Close Messenger" : "Back to Lobby"}
          data-testid="messenger-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <img src={MEGA_LOGO_URL} alt="Mega Chat" className="h-6 object-contain" data-testid="messenger-topbar-logo" />
        <div className="flex flex-col">
          <div className="text-sm font-black leading-tight tracking-wider" style={{ fontFamily: "Impact, sans-serif" }}>MEGA CHAT</div>
          <div className="text-[9px] font-extrabold text-yellow-200 tracking-widest -mt-0.5">MESSENGER</div>
        </div>
        <div className="flex-1" />
        {/* Enter Chat Rooms button (always visible) */}
        <button
          onClick={goToRooms}
          className="flex items-center gap-1 bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-black text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full shadow border-2 border-yellow-500 uppercase tracking-wide"
          title="Enter public chat rooms"
          data-testid="messenger-enter-rooms-btn"
        >
          <Home className="w-3 h-3" />
          <span className="hidden xs:inline sm:inline">Chat Rooms</span>
          <span className="xs:hidden sm:hidden">Rooms</span>
        </button>
        {/* Install App button — hidden once installed */}
        {!installed && (canInstall || isIos) && (
          <button
            onClick={handleInstall}
            className="flex items-center gap-1 bg-white hover:bg-purple-50 text-purple-900 font-black text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full shadow border-2 border-white uppercase tracking-wide"
            title="Install Messenger to your phone"
            data-testid="messenger-install-app-btn"
          >
            {isIos ? <Share2 className="w-3 h-3" /> : <Download className="w-3 h-3" />}
            <span className="hidden sm:inline">Install App</span>
            <span className="sm:hidden">Install</span>
          </button>
        )}
      </div>
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className={`w-full sm:w-60 flex-shrink-0 ${selected ? "hidden sm:block" : "block"}`}>
          <BuddyList
            buddies={buddies}
            onSelect={setSelected}
            selectedId={selectedId}
            myPresence={myPresence}
            onChangeStatus={changeStatus}
            currentUser={currentUser}
            onNewGroup={() => setShowNewGroup(true)}
            onOpenContacts={() => setShowContacts(true)}
          />
        </div>
        <div className={`flex-1 flex-col ${selected ? "flex" : "hidden sm:flex"}`}>
          {/* Mobile back-to-buddies bar */}
          {selected && (
            <button
              onClick={() => setSelected(null)}
              className="sm:hidden flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-purple-800 bg-yellow-100 border-b border-purple-200"
              data-testid="messenger-back-to-buddies"
            >
              <ArrowLeft className="w-3 h-3" /> Buddies
            </button>
          )}
          <ConversationView
            conversation={selected}
            currentUser={currentUser}
            token={token}
            wsRef={wsRef}
            onMessageSent={() => loadConversations()}
          />
        </div>
      </div>
      {showNewGroup && (
        <NewGroupModal
          buddies={buddies}
          onClose={() => setShowNewGroup(false)}
          onCreate={createGroup}
        />
      )}
      {showIosHelp && (
        <IosInstallHelp onClose={() => setShowIosHelp(false)} />
      )}
      {showContacts && (
        <ContactsPanel
          token={token}
          currentUser={currentUser}
          onClose={() => setShowContacts(false)}
          onStartChat={(conv) => setSelected(conv)}
          onImported={() => loadBuddies()}
        />
      )}
    </div>
  );
};

export default MessengerPage;
